import React from 'react'
import { Autocomplete, Checkbox, Chip, SxProps, TextField, useMediaQuery, useTheme } from '@mui/material'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import { useTranslation } from 'react-i18next'

interface AutocompleteFetchProps<T> {
  selectedItems: T[]
  setSelectedItems: (items: T[]) => void
  options: T[]
  getLabel: (item: T) => string
  getId: (item: T) => string
  labelTranslationKey: string
  onSearchChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
  width?: string | number
  multiple?: boolean
  sxProps?: SxProps
}

export const AutocompleteFetch = <T,>({
  selectedItems,
  setSelectedItems,
  options,
  getLabel,
  getId,
  labelTranslationKey,
  onSearchChange,
  width,
  multiple = true,
  sxProps,
}: AutocompleteFetchProps<T>) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />
  const checkedIcon = <CheckBoxIcon fontSize="small" />

  return (
    <Autocomplete
      multiple={multiple}
      disableCloseOnSelect={multiple}
      disableClearable
      limitTags={2}
      sx={{ width: width || '100%', ...sxProps }}
      value={selectedItems}
      onChange={(_, value) => {
        if (multiple) {
          setSelectedItems(value as T[])
        } else {
          setSelectedItems(value ? [value as T] : [])
        }
      }}
      getOptionLabel={getLabel}
      options={options}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={getId(option)}
            label={getLabel(option)}
            sx={{
              maxWidth: isSmallScreen ? '100px !important' : '200px !important',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          />
        ))
      }
      renderOption={(props, option, { selected }) => {
        const { key, ...optionProps } = props
        return (
          <li key={key} {...optionProps}>
            {multiple && (
              <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
            )}
            {getLabel(option)}
          </li>
        )
      }}
      isOptionEqualToValue={(option, value) => getId(option) === getId(value)}
      renderInput={({ InputLabelProps, ...params }) => (
        <TextField
          onChange={onSearchChange ? onSearchChange : undefined}
          {...params}
          label={t(labelTranslationKey)}
          size="small"
        />
      )}
      ListboxProps={{ style: { maxHeight: 200 } }}
    />
  )
}
